import { useCallback, useEffect, useRef } from 'react'
import { ButtonWithDropDownMenu, NormalFormDateField, NormalFormNumberField, NormalFormSelectionField, NormalFormTextField } from '../molecules'
import { useUserLanguage } from '@/customHooks'
import { CreateRentChargeInput, RentChargeTypeOption } from '@/graphql/generated'
import textsData from '@/textData/CreateContractsTexts.json'

type DropDownMenuActionsProps = {
  delete: (id: number) => void
}

type CreateUnitSimpleFormProps = {
  onChange: (data: CreateRentChargeInput) => void
  id?: number
  dropDownMenuActions?: DropDownMenuActionsProps
  defaultData?: CreateRentChargeInput
}

export const CreateRentChargeFormControlled = ({ onChange, id, dropDownMenuActions, defaultData }: CreateUnitSimpleFormProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.rentCharges.form : textsData.en.rentCharges.form

  const rentDataRef = useRef<CreateRentChargeInput>(defaultData || {
    rentChargeType: RentChargeTypeOption.RentIncome,
    description: '',
    amountValue: '',
    executionDate: new Date(), // Default to current date if no defaultData
  });

  // Update ref when defaultData changes (e.g., when a charge is edited)
  useEffect(() => {
    if (defaultData) {
      rentDataRef.current = defaultData;
    }
  }, [defaultData]);

  const dropDownMenuOptions = [
    {
      item: texts.dropdownMenuOptions[1],
      action: () => dropDownMenuActions?.delete(id ?? 0)
    }
  ]

  const rentChargeOptions = [
    {
      name: texts.option.option1,
      value: RentChargeTypeOption.RentIncome,
    },
    {
      name: texts.option.option2,
      value: RentChargeTypeOption.SecurityDeposit,
    },
    {
      name: texts.option.option3,
      value: RentChargeTypeOption.PetDeposit,
    }
  ]

  const onChangeHandle = useCallback((field: keyof CreateRentChargeInput, value: string | Date | null) => {
    let processedValue: string | Date | null = value;
    if (field === 'executionDate' && typeof value === 'string') {
      processedValue = value ? new Date(value) : null;
    }
    rentDataRef.current = {
      ...rentDataRef.current,
      [field]: processedValue,
    }
    onChange(rentDataRef.current)
  }, [onChange])

  return (
    <div className="bg-[var(--color-light)] rounded-lg shadow-[0_5px_10px_rgba(0,0,0,0.1)] p-4 w-full flex flex-col gap-4 box-border">
      <div className="flex flex-wrap items-center gap-4 w-full rounded-[8px]">
        <div className="w-[35px] h-[35px] flex justify-center items-center text-[var(--color-primary-1)] text-base font-bold border border-[var(--color-primary-1)] rounded-full shrink-0">
          <span>{(id ?? 0) + 1}</span>
        </div>
        
        <div className="flex-1 min-w-[200px] md:flex-1">
          <NormalFormSelectionField<RentChargeTypeOption>
            label={texts.rentChargeType.label}
            name={texts.rentChargeType.name}
            options={rentChargeOptions}
            value={defaultData?.rentChargeType ?? rentDataRef.current.rentChargeType}
            onChange={(value) => onChangeHandle('rentChargeType', value)}
            required
          />
        </div>

        <div className="flex-1 min-w-[200px] md:flex-1">
          <NormalFormNumberField
            name="amount"
            label={texts.amount.label}
            value={defaultData?.amountValue ?? rentDataRef.current.amountValue}
            onChange={(value) => onChangeHandle('amountValue', value)}
            required
          />
        </div>

        <div className="flex-1 min-w-[200px] md:flex-[2]">
          <NormalFormTextField
            name="description"
            label={texts.description.label}
            placeholder={texts.description.placeholder}
            value={defaultData?.description ?? rentDataRef.current.description}
            onChange={(value) => onChangeHandle('description', value)}
          />
        </div>

        <div className="flex-1 min-w-[200px] md:flex-1">
          <NormalFormDateField
            name='executionDate'
            label={texts.executionDate.label}
            value={(defaultData?.executionDate ?? rentDataRef.current.executionDate) instanceof Date
              ? (defaultData?.executionDate ?? rentDataRef.current.executionDate).toISOString()
              : ''}
            onChange={(value) => onChangeHandle('executionDate', value)}
          />
        </div>

        <div className="shrink-0">
          <ButtonWithDropDownMenu
            options={dropDownMenuOptions}
            position='left'
          />
        </div>
      </div>
    </div>
  )
}